import { TFunction } from 'i18next'
import { z } from 'zod'

import { AnswerType, BookingQuestion, PricingType, SelectType } from '@/api/booking-types'

export const QUESTION_TITLE_MAX_LENGTH = 255
export const QUESTION_MODAL_MAX_LENGTH = 1000
export const ANSWER_TITLE_MAX_LENGTH = 255
export const ANSWER_PRICE_MAX_LENGTH = 255

export const ANSWER_TYPES = [
  'button',
  'input',
  'image',
  'time picker',
] as const satisfies readonly AnswerType[]

export const SELECT_TYPES = ['single', 'multiple'] as const satisfies readonly SelectType[]

export const PRICING_TYPES = [
  'add',
  'discount',
  'custom price',
] as const satisfies readonly PricingType[]

export const CUSTOM_PRICE: PricingType = 'custom price'

const NUMERIC_PRICE_REGEX = /^\d+(\.\d+)?$/

export const IMAGE_LIMIT_DEFAULT = 3
export const IMAGE_LIMIT_MAX = 10

export const ANSWER_TYPE_LABEL_KEYS = {
  button: 'button',
  image: 'image',
  input: 'input',
  'time picker': 'timePicker',
} as const

export const PRICING_TYPE_LABEL_KEYS = {
  add: 'add',
  'custom price': 'customPrice',
  discount: 'discount',
} as const

type QuestionT = TFunction<['organisation', 'common']>

export const makeAnswerSchema = (t: QuestionT) => {
  const maxLength = (max: number) => t('organisation:bookingTypes.validation.maxLength', { max })

  return z.object({
    id: z.number().optional(),
    placeholder: z.string(),
    pricingType: z.enum(PRICING_TYPES),
    pricingValue: z
      .string()
      .trim()
      .max(ANSWER_PRICE_MAX_LENGTH, maxLength(ANSWER_PRICE_MAX_LENGTH)),
    title: z
      .string()
      .trim()
      .min(1, t('common:validation.required'))
      .max(ANSWER_TITLE_MAX_LENGTH, maxLength(ANSWER_TITLE_MAX_LENGTH)),
  })
}

export type AnswerFormData = z.infer<ReturnType<typeof makeAnswerSchema>>

export const makeAnswerDraftSchema = (t: QuestionT) =>
  makeAnswerSchema(t).superRefine((answer, ctx) => {
    if (
      answer.pricingType !== CUSTOM_PRICE &&
      answer.pricingValue &&
      !NUMERIC_PRICE_REGEX.test(answer.pricingValue)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t('organisation:bookingTypes.validation.priceInvalid'),
        path: ['pricingValue'],
      })
    }
  })

export const makeQuestionFormSchema = (t: QuestionT) => {
  const maxLength = (max: number) => t('organisation:bookingTypes.validation.maxLength', { max })
  const answerSchema = makeAnswerSchema(t)

  return z
    .object({
      answers: z.array(answerSchema),
      answersType: z.enum(ANSWER_TYPES),
      imageLimit: z.string().trim(),
      isRequired: z.boolean(),
      modal: z.string().trim().max(QUESTION_MODAL_MAX_LENGTH, maxLength(QUESTION_MODAL_MAX_LENGTH)),
      selectType: z.enum(SELECT_TYPES),
      title: z
        .string()
        .trim()
        .min(1, t('common:validation.required'))
        .max(QUESTION_TITLE_MAX_LENGTH, maxLength(QUESTION_TITLE_MAX_LENGTH)),
    })
    .superRefine((data, ctx) => {
      if (data.answersType === 'image') {
        const limit = Number(data.imageLimit)
        if (!Number.isInteger(limit) || limit < 1 || limit > IMAGE_LIMIT_MAX) {
          ctx.addIssue({
            code: 'custom',
            message: t('organisation:bookingTypes.validation.imageLimitInvalid', {
              max: IMAGE_LIMIT_MAX,
            }),
            path: ['imageLimit'],
          })
        }
      }

      if (data.answersType !== 'button') return

      data.answers.forEach((answer, index) => {
        if (answer.pricingType === CUSTOM_PRICE) {
          if (data.selectType === 'multiple') {
            ctx.addIssue({
              code: 'custom',
              message: t('organisation:bookingTypes.validation.customPriceOnMultiple'),
              path: ['answers', index, 'pricingType'],
            })
          }
          return
        }

        if (answer.pricingValue && !NUMERIC_PRICE_REGEX.test(answer.pricingValue)) {
          ctx.addIssue({
            code: 'custom',
            message: t('organisation:bookingTypes.validation.priceInvalid'),
            path: ['answers', index, 'pricingValue'],
          })
        }
      })
    })
}

export type QuestionFormData = z.infer<ReturnType<typeof makeQuestionFormSchema>>

export type QuestionFormAnswer = QuestionFormData['answers'][number]

export const getAnswersType = (question?: BookingQuestion): AnswerType =>
  question?.answers[0]?.type ?? 'button'

export const getEmptyAnswer = (): QuestionFormAnswer => ({
  placeholder: '',
  pricingType: 'add',
  pricingValue: '0',
  title: '',
})

export const getQuestionFormDefaults = (question?: BookingQuestion): QuestionFormData => ({
  answers: [...(question?.answers ?? [])]
    .sort((a, b) => a.order - b.order || a.id - b.id)
    .map((answer) => ({
      id: answer.id,
      placeholder: answer.placeholder ?? '',
      pricingType: answer.pricingType ?? 'add',
      pricingValue: answer.pricingValue ?? '',
      title: answer.title,
    })),
  answersType: getAnswersType(question),
  imageLimit: String(question?.imageLimit || IMAGE_LIMIT_DEFAULT),
  isRequired: question?.isRequired ?? false,
  modal: question?.modal ?? '',
  selectType: question?.selectType ?? 'single',
  title: question?.title ?? '',
})
